/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { CloseOutlined } from '@ant-design/icons'
import { Modal, Tag } from 'antd'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import useWindowSize from 'src/hooks/useWindowSize'
import { useModalScroll } from 'src/hooks/useModalScroll'
import {
  VideographerProvider,
  useVideographerContext,
} from './VideographerContext'
import VideographerModalContent from './VideographerModalContent'

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
    padding: '16px',
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

interface VideographerModalInnerProps {
  isOpen: boolean
  onClose: () => void
  isMobile: boolean
}

const VideographerModalInner: React.FC<VideographerModalInnerProps> = ({
  isOpen,
  onClose,
  isMobile,
}) => {
  const { state, onSubmit, onCancel, onBack } = useVideographerContext()

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  const getHeader = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 12px;
        `}
      >
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Invite Videographer
        </Text>
        <Tag color='gold'>Beta</Tag>
      </div>
    )
  }, [])

  const getFooter = useCallback(() => {
    if (state.step === 'team-selection') {
      return (
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            gap: 16px;
            width: 50%;
            margin-left: auto;
            ${isMobile &&
            css`
              width: 100%;
            `}
          `}
        >
          <Button
            text='Cancel'
            onClick={onCancel}
            variant='secondary'
            disabled={state.isLoading}
          />
        </div>
      )
    }

    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: 50%;
          margin-left: auto;
          ${isMobile &&
          css`
            width: 100%;
            flex-direction: column-reverse;
          `}
        `}
      >
        <Button
          text='Back'
          onClick={onBack}
          variant='secondary'
          disabled={state.isLoading}
        />
        <Button
          text='Send Invite'
          onClick={onSubmit}
          variant='primary'
          isLoading={state.isLoading}
          disabled={state.isLoading || !state.email.trim()}
          style={css`
            white-space: nowrap;
          `}
        />
      </div>
    )
  }, [
    isMobile,
    state.step,
    state.isLoading,
    state.email,
    onSubmit,
    onCancel,
    onBack,
  ])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100%' : 'auto'} !important;
        max-height: ${isMobile ? '100%' : '90vh'};
        margin: ${isMobile ? '0' : '24px auto'};
        padding: 0;

        .ant-modal-content {
          height: ${isMobile ? '100%' : 'auto'} !important;
          max-height: ${isMobile ? '100%' : '90vh'};
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }
      `}
      width={isMobile ? '100%' : 500}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={onClose}
      centered={!isMobile}
      styles={modalStyles}
      maskClosable={false}
    >
      <VideographerModalContent />
    </Modal>
  )
}

export interface VideographerModalRef {
  openModal: () => void
  closeModal: () => void
}

interface VideographerModalProps {
  gameId: string
  homeRosterId: string | null
  visitorRosterId: string | null
  homeTeamName: string
  visitorTeamName: string
  onSuccess: () => void
  homeTeamImage?: string | number | null
  visitorTeamImage?: string | number | null
  preSelectedTeam?: 'home' | 'visitor'
  onModalClose?: () => void
}

const VideographerModal = forwardRef<
  VideographerModalRef,
  VideographerModalProps
>(
  (
    {
      gameId,
      homeRosterId,
      visitorRosterId,
      homeTeamName,
      visitorTeamName,
      onSuccess,
      homeTeamImage,
      visitorTeamImage,
      preSelectedTeam,
      onModalClose,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const { width } = useWindowSize()
    const isMobile = useMemo(() => width <= 768, [width])
    const { openModal: scrollOpen, closeModal: scrollClose } = useModalScroll()

    const openModal = useCallback(() => {
      setIsOpen(true)
      scrollOpen()
    }, [scrollOpen])

    const closeModal = useCallback(() => {
      setIsOpen(false)
      scrollClose()
      onModalClose?.()
    }, [scrollClose, onModalClose])

    useImperativeHandle(ref, () => ({
      openModal,
      closeModal,
    }))

    useEffect(() => {
      return () => {
        if (isOpen) {
          scrollClose()
        }
      }
    }, [isOpen, scrollClose])

    if (!isOpen) {
      return null
    }

    return (
      <VideographerProvider
        gameId={gameId}
        homeRosterId={homeRosterId}
        visitorRosterId={visitorRosterId}
        homeTeamName={homeTeamName}
        visitorTeamName={visitorTeamName}
        onClose={closeModal}
        onSuccess={onSuccess}
        homeTeamImage={homeTeamImage}
        visitorTeamImage={visitorTeamImage}
        preSelectedTeam={preSelectedTeam}
      >
        <VideographerModalInner
          isOpen={isOpen}
          onClose={closeModal}
          isMobile={isMobile}
        />
      </VideographerProvider>
    )
  }
)

VideographerModal.displayName = 'VideographerModal'

export default VideographerModal
